import React, { useState } from 'react';
import { Item } from '@/stores/canvasStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  ExternalLink,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  TrendingUp,
  Zap,
  Target,
  Sparkles,
  Loader2,
} from 'lucide-react';

interface ScrapedCreativeContent {
  scraped_creative_id?: string;
  platform?: string;
  account_handle?: string;
  account_name?: string;
  content_url?: string;
  thumbnail_url?: string;
  content_type?: string;
  hook_text?: string;
  caption?: string;
  views?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  saves?: number;
  engagement_rate?: number;
  published_at?: string;
  // Motion-style scores
  creative_format?: string;
  hook_score?: number | null;
  hold_score?: number | null;
  engagement_score?: number | null;
  overall_creative_score?: number | null;
  hook_tactic?: string;
  messaging_angle?: string;
  target_persona?: string;
  why_selected?: string | null;
}

interface ScrapedCreativeCardProps {
  item: Item;
}

const PLATFORM_COLORS: Record<string, string> = {
  tiktok: 'from-gray-900 to-gray-800',
  instagram: 'from-purple-600 to-pink-500',
  facebook: 'from-blue-600 to-blue-500',
};

const PLATFORM_LABELS: Record<string, string> = {
  tiktok: 'TikTok',
  instagram: 'Instagram',
  facebook: 'Facebook',
};

function formatNumber(num: number | undefined): string {
  if (!num) return '0';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toString();
}

function scoreColor(score: number | null | undefined): string {
  if (!score) return 'text-muted-foreground';
  if (score >= 80) return 'text-emerald-500';
  if (score >= 60) return 'text-green-500';
  if (score >= 40) return 'text-yellow-500';
  return 'text-red-500';
}

function scoreBg(score: number | null | undefined): string {
  if (!score) return 'bg-muted';
  if (score >= 80) return 'bg-emerald-500/15';
  if (score >= 60) return 'bg-green-500/15';
  if (score >= 40) return 'bg-yellow-500/15';
  return 'bg-red-500/15';
}

function formatFormatLabel(format: string | undefined): string {
  if (!format) return '';
  return format
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

export function ScrapedCreativeCard({ item }: ScrapedCreativeCardProps) {
  const [showDetail, setShowDetail] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const content = item.content as ScrapedCreativeContent;

  const engagementPct = content.engagement_rate
    ? (content.engagement_rate * 100).toFixed(1)
    : '0';

  const hasScores = content.overall_creative_score != null;
  const isAnalyzing = !hasScores;

  const handleOpenUrl = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (content.content_url) {
      window.open(content.content_url, '_blank', 'noopener');
    }
  };

  return (
    <>
      <div
        className="w-full h-full rounded-lg overflow-hidden card-shadow bg-card relative group flex flex-col"
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
      >
        {/* Thumbnail / Header */}
        <div className={cn(
          'relative h-[45%] min-h-[80px] bg-gradient-to-br flex items-center justify-center overflow-hidden',
          PLATFORM_COLORS[content.platform || ''] || 'from-gray-700 to-gray-600'
        )}>
          {content.thumbnail_url ? (
            <img
              src={content.thumbnail_url}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <Sparkles className="w-8 h-8 text-white/40" />
          )}

          {/* Platform badge */}
          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/50 text-white text-[10px] font-medium backdrop-blur-sm">
            {PLATFORM_LABELS[content.platform || ''] || content.platform}
          </div>

          {/* Overall score badge */}
          {hasScores && (
            <div className={cn(
              'absolute top-2 right-2 w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold backdrop-blur-sm border',
              content.overall_creative_score! >= 80
                ? 'bg-emerald-500/80 border-emerald-400 text-white'
                : content.overall_creative_score! >= 60
                ? 'bg-green-500/80 border-green-400 text-white'
                : content.overall_creative_score! >= 40
                ? 'bg-yellow-500/80 border-yellow-400 text-white'
                : 'bg-red-500/80 border-red-400 text-white'
            )}>
              {content.overall_creative_score}
            </div>
          )}

          {isAnalyzing && (
            <div className="absolute top-2 right-2 px-2 py-1 rounded-full bg-black/50 text-white text-[10px] backdrop-blur-sm flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              Scoring...
            </div>
          )}

          {/* Engagement rate badge */}
          <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-semibold backdrop-blur-sm flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            {engagementPct}%
          </div>

          {/* Creative format badge */}
          {content.creative_format && (
            <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-white/20 text-white text-[10px] backdrop-blur-sm">
              {formatFormatLabel(content.creative_format)}
            </div>
          )}
        </div>

        {/* Content body */}
        <div className="flex-1 p-2.5 flex flex-col gap-1.5 min-h-0">
          {/* Account */}
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-semibold text-foreground truncate">
              @{content.account_handle}
            </span>
            {content.account_name && (
              <span className="text-[10px] text-muted-foreground truncate">
                {content.account_name}
              </span>
            )}
          </div>

          {/* Hook text */}
          {content.hook_text && (
            <p className="text-[11px] text-foreground/80 leading-snug line-clamp-2">
              "{content.hook_text}"
            </p>
          )}

          {/* Motion scores row */}
          {hasScores && (
            <div className="flex gap-1 mt-auto">
              <ScorePill label="Hook" score={content.hook_score} icon={Zap} />
              <ScorePill label="Hold" score={content.hold_score} icon={Target} />
              <ScorePill label="Eng" score={content.engagement_score} icon={TrendingUp} />
            </div>
          )}

          {/* Metrics row */}
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-auto">
            <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" />{formatNumber(content.views)}</span>
            <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" />{formatNumber(content.likes)}</span>
            <span className="flex items-center gap-0.5"><MessageCircle className="w-3 h-3" />{formatNumber(content.comments)}</span>
            {(content.shares || 0) > 0 && (
              <span className="flex items-center gap-0.5"><Share2 className="w-3 h-3" />{formatNumber(content.shares)}</span>
            )}
            {(content.saves || 0) > 0 && (
              <span className="flex items-center gap-0.5"><Bookmark className="w-3 h-3" />{formatNumber(content.saves)}</span>
            )}
          </div>

          {/* Why selected summary */}
          {content.why_selected && (
            <p className="text-[10px] text-muted-foreground leading-snug line-clamp-2 mt-0.5 italic">
              {content.why_selected}
            </p>
          )}
        </div>

        {/* Hover controls */}
        <div className={cn(
          "absolute top-2 right-12 flex gap-1.5 transition-opacity",
          showControls ? "opacity-100" : "opacity-0"
        )}>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); setShowDetail(true); }}
            className="w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleOpenUrl}
            className="w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Detail modal */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4" />
              Creative Breakdown — @{content.account_handle}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Thumbnail */}
            {content.thumbnail_url && (
              <img
                src={content.thumbnail_url}
                alt=""
                className="w-full rounded-lg max-h-48 object-cover"
              />
            )}

            {/* Scores grid */}
            {hasScores && (
              <div className="grid grid-cols-4 gap-2">
                <ScoreCard label="Hook" score={content.hook_score!} />
                <ScoreCard label="Hold" score={content.hold_score!} />
                <ScoreCard label="Engagement" score={content.engagement_score!} />
                <ScoreCard label="Overall" score={content.overall_creative_score!} highlight />
              </div>
            )}

            {/* Classifications */}
            <div className="space-y-2">
              {content.creative_format && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground w-24 shrink-0">Format:</span>
                  <span className="font-medium">{formatFormatLabel(content.creative_format)}</span>
                </div>
              )}
              {content.hook_tactic && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground w-24 shrink-0">Hook Tactic:</span>
                  <span className="font-medium">{formatFormatLabel(content.hook_tactic)}</span>
                </div>
              )}
              {content.messaging_angle && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground w-24 shrink-0">Angle:</span>
                  <span className="font-medium">{formatFormatLabel(content.messaging_angle)}</span>
                </div>
              )}
              {content.target_persona && (
                <div className="flex items-start gap-2 text-sm">
                  <span className="text-muted-foreground w-24 shrink-0">Persona:</span>
                  <span>{content.target_persona}</span>
                </div>
              )}
            </div>

            {/* Hook text */}
            {content.hook_text && (
              <div className="space-y-1">
                <span className="text-xs font-medium text-muted-foreground">Hook</span>
                <p className="text-sm bg-muted/50 rounded-md p-2 italic">"{content.hook_text}"</p>
              </div>
            )}

            {/* Why selected */}
            {content.why_selected && (
              <div className="space-y-1">
                <span className="text-xs font-medium text-muted-foreground">Why This Is a Top Performer</span>
                <p className="text-sm">{content.why_selected}</p>
              </div>
            )}

            {/* Metrics detail */}
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Performance Metrics</span>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <MetricCell icon={Eye} label="Views" value={formatNumber(content.views)} />
                <MetricCell icon={Heart} label="Likes" value={formatNumber(content.likes)} />
                <MetricCell icon={MessageCircle} label="Comments" value={formatNumber(content.comments)} />
                <MetricCell icon={Share2} label="Shares" value={formatNumber(content.shares)} />
                <MetricCell icon={Bookmark} label="Saves" value={formatNumber(content.saves)} />
                <MetricCell icon={TrendingUp} label="Eng. Rate" value={`${engagementPct}%`} />
              </div>
            </div>

            {/* Link to original */}
            {content.content_url && (
              <a
                href={content.content_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                View original on {PLATFORM_LABELS[content.platform || ''] || content.platform}
              </a>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================
// Sub-components
// ============================================================

function ScorePill({ label, score, icon: Icon }: { label: string; score: number | null | undefined; icon: React.ElementType }) {
  return (
    <div className={cn(
      'flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium',
      scoreBg(score), scoreColor(score)
    )}>
      <Icon className="w-3 h-3" />
      {score ?? '—'}
    </div>
  );
}

function ScoreCard({ label, score, highlight }: { label: string; score: number; highlight?: boolean }) {
  return (
    <div className={cn(
      'rounded-lg p-2 text-center',
      highlight ? 'bg-primary/10 border border-primary/20' : 'bg-muted/50'
    )}>
      <div className={cn('text-2xl font-bold', scoreColor(score))}>{score}</div>
      <div className="text-[10px] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function MetricCell({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="flex items-center gap-1.5 bg-muted/30 rounded px-2 py-1.5">
      <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <div>
        <div className="text-xs font-medium">{value}</div>
        <div className="text-[10px] text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}
